// The cash report as bars - one series, olive, because the
// question is "how much came in", not "which kind". The bars are a
// picture of the table below it, which stays the full, keyboard-readable
// record: ninety focusable slots would cost ninety tab stops and say
// nothing the table does not.

import { fmtCents } from "../../../lib/money";
import type { BucketRow } from "../lib/reportRanges";

export interface ChartRow extends BucketRow {
  /** What a person calls the bucket ("Aug 12", "Jul 2026"). */
  label: string;
}

// Clean axis ceilings: 1/2/2.5/5 times a power of ten, whole dollars.
export function niceMaxCents(maxCents: number): number {
  if (maxCents <= 0) return 100;
  const steps = [100, 200, 250, 500];
  let scale = 1;
  for (;;) {
    for (const s of steps) {
      if (maxCents <= s * scale) return s * scale;
    }
    scale *= 10;
  }
}

function tick(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

// Every ~nth label so ~7 survive: 30 days of x-axis in full is noise.
function labelStep(count: number): number {
  return Math.max(1, Math.ceil(count / 7));
}

// The card clips at its edge, so a centered tooltip on an outer bar
// would lose its leading figures - outer thirds pin to their own side.
function tipSide(i: number, count: number): string {
  if (count < 8) return "";
  if (i < count / 4) return " tip-left";
  if (i >= count - count / 4) return " tip-right";
  return "";
}

export function CollectedBars({ rows }: { rows: ChartRow[] }) {
  const max = niceMaxCents(Math.max(...rows.map((r) => r.collectedCents)));
  const step = labelStep(rows.length);

  return (
    <div
      className="cc-chart"
      role="img"
      aria-label={`Collected money, ${rows.length} bars - the table below carries every figure`}
    >
      <div className="cc-chart-plot">
        {[1, 0.5, 0].map((f) => (
          <div key={f} className="cc-chart-grid" style={{ bottom: `${f * 100}%` }}>
            <span className="cc-chart-tick">{tick(max * f)}</span>
          </div>
        ))}
        <div className="cc-chart-bars">
          {rows.map((r, i) => (
            <div key={r.bucket} className={`cc-chart-slot${tipSide(i, rows.length)}`}>
              <div
                className="cc-chart-bar"
                style={{ height: `${(r.collectedCents / max) * 100}%` }}
              />
              <div className="cc-chart-tip">
                <div className="t">{r.label}</div>
                <div className="v tnum">{fmtCents(r.collectedCents)}</div>
                <div className="d">
                  Online {fmtCents(r.stripeCents)}, by hand {fmtCents(r.manualCents)}
                  {r.returnedCents > 0 ? `, returned ${fmtCents(r.returnedCents)}` : ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="cc-chart-x">
        {rows.map((r, i) => (
          <span key={r.bucket} className="cc-chart-xlabel">
            {i % step === 0 ? r.label : ""}
          </span>
        ))}
      </div>
    </div>
  );
}
